import { CadCommandType } from '../../../command/CadCommandType';
import { resolveRevolveFeatureKind } from '../../definition/revolve/RevolveFeatureKindResolver';
import {createFeatureReferenceBinding} from '../../../utils/feature/createFeatureReferenceBinding';
import type {FeatureReferenceBinding} from '../../binding/FeatureReferenceBinding';
import {RevolveFeatureDefinitionModel} from '../../definition/revolve/RevolveFeatureDefinition';
import {BossRevolveFeaturePrototype} from '../../prototype/revolve/BossRevolveFeaturePrototype';
import {CutRevolveFeaturePrototype} from '../../prototype/revolve/CutRevolveFeaturePrototype';
import type {RevolveFeaturePrototype} from '../../prototype/revolve/RevolveFeaturePrototype';
import {
    FeatureCommandAdapter,
    type FeatureCommandBuildContext,
    type FeatureCommandDependencyContext,
    type FeatureCommandDependencyInfo,
} from '../FeatureCommandAdapter';
import {
    readCommandStringArrayParam,
    uniqueNonEmptyStrings,
} from '../../../utils/feature/featureCommandDependencyUtils';
import { resolveSketchProfileSourceSketchId } from '../../../utils/feature/resolveSketchProfileSourceSketchId';

/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/feature/command/revolve 目录；
 * 2. 负责在旋转命令与旋转特征原型之间做双向转换；
 * 3. 该适配器是旋转特征进入 prototype / projection / edit session 主链的唯一入口。
 */

/**
 * 旋转命令适配器。
 */
export class RevolveCommandAdapter extends FeatureCommandAdapter<RevolveFeaturePrototype> {
    /**
     * @inheritdoc
     */
    public supports(command: CadCommand): boolean {
        return command.type === CadCommandType.REVOLVE;
    }

    /**
     * @inheritdoc
     */
    public createPrototypeFromCommand(command: CadCommand): RevolveFeaturePrototype | null {
        if (!this.supports(command)) {
            return null;
        }
        const params = structuredClone(command.params as RevolveParams);
        const featureKind = resolveRevolveFeatureKind(params);
        const definition = RevolveFeatureDefinitionModel.fromParams({
            featureKind,
            commandParams: params,
        });
        const bindings = this.buildBindingsFromParams(params);
        const sourceSketchId = resolveSketchProfileSourceSketchId({
            definitionSourceSketchId: definition.getSourceSketchId(),
            bindings,
        });
        const PrototypeCtor = featureKind === 'cut-revolve'
            ? CutRevolveFeaturePrototype
            : BossRevolveFeaturePrototype;
        return new PrototypeCtor({
            featureId: command.id,
            definition,
            bindings,
            sourceSketchId,
        });
    }

    /**
     * @inheritdoc
     */
    public collectDependencyInfo(
        context: FeatureCommandDependencyContext<RevolveFeaturePrototype>,
    ): FeatureCommandDependencyInfo {
        const dependencyInfo = super.collectDependencyInfo(context);
        const targetSolidIds = readCommandStringArrayParam(context.command, 'targetSolidIds');
        const params = context.command.params as RevolveParams;
        const modelAxisOwnerObjectId = params.axisReference?.kind === 'model_edge'
            ? params.axisReference.sourceRef.ownerObjectId
            : null;

        return {
            ...dependencyInfo,
            referencedModelIds: uniqueNonEmptyStrings([
                ...dependencyInfo.referencedModelIds,
                ...targetSolidIds,
                modelAxisOwnerObjectId,
            ]),
        };
    }

    /**
     * @inheritdoc
     */
    public createCommandFromPrototype(
        context: FeatureCommandBuildContext<RevolveFeaturePrototype>,
    ): CadCommand {
        const featureKind = context.prototype.getDefinition().getFeatureKind();
        return {
            id: context.prototype.getFeatureId(),
            type: CadCommandType.REVOLVE,
            clientId: context.clientId,
            timestamp: context.timestamp ?? Date.now(),
            params: {
                ...context.prototype.getDefinition().getCommandParams(),
                featureKind,
                operation: featureKind === 'cut-revolve' ? 'cut' : 'boss',
                referenceBindings: context.prototype.getBindings().map((binding) => binding.serialize()),
            },
        };
    }

    /**
     * 从命令参数恢复输入绑定。
     * @param params 命令参数。
     * @returns 输入绑定集合。
     */
    private buildBindingsFromParams(params: RevolveParams): FeatureReferenceBinding[] {
        const bindings = (params.referenceBindings ?? []).map((binding) => createFeatureReferenceBinding(binding));
        const hasProfileBinding = bindings.some((binding) => binding.kind === 'sketch-profile');
        const hasAxisBinding = bindings.some((binding) => binding.kind === 'sketch-sub-entity');
        const profileSketchId = typeof params.sketchId === 'string' ? params.sketchId.trim() : '';
        if (!hasProfileBinding && params.sourceType === 'region_geometry' && profileSketchId.length > 0) {
            bindings.push(createFeatureReferenceBinding({
                kind: 'sketch-profile',
                sketchId: profileSketchId,
                contourKeys: Array.isArray(params.contourKeys)
                    ? params.contourKeys.filter((item): item is string => typeof item === 'string')
                    : undefined,
                regionIdentities: Array.isArray(params.regionIdentities) ? params.regionIdentities : undefined,
                fallbackRegionGeometries: Array.isArray(params.regionGeometries)
                    ? params.regionGeometries
                    : (params.regionGeometry ? [params.regionGeometry] : undefined),
                runtimeSketchPlane: params.runtimeSketchPlane,
                runtimeSketchEntities: params.runtimeSketchEntities,
            }));
        }
        if (!hasAxisBinding && params.axisReference?.kind === 'sketch_edge') {
            bindings.push(createFeatureReferenceBinding({
                kind: 'sketch-sub-entity',
                sketchId: params.axisReference.sourceRef.ownerSketchId,
                sourceRef: params.axisReference.sourceRef,
            }));
        }
        return bindings;
    }
}
